import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../style/EditDewormingPage.css';
import dewormingMethods from '../../data/dewormingMethods';
import dewormingTypes from '../../data/dewormingTypes';
import { useTranslation } from 'react-i18next'; // Import translation hook
import i18n from '../../i18n'; // Import i18n instance

const EditDewormingPage = () => {
    const { id, dewormingId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(); // Initialize translation hook
    const [deworming, setDeworming] = useState({ method: '', customMethod: '', type: '' });
    const [loading, setLoading] = useState(false);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchDewormingDetails = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings/${dewormingId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(t('deworming.fetchFailed')); // Use translation for error
                }

                const data = await response.json();
                setDeworming({
                    method: data.method || '',
                    customMethod: data.customMethod || '',
                    type: data.type || '',
                });
            } catch (error) {
                console.error(error);
            }
        };

        fetchDewormingDetails();
    }, [id, dewormingId, baseUrl, t]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDeworming((prevDeworming) => ({
            ...prevDeworming,
            [name]: value,
        }));
    };

    const handleUpdateDeworming = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const methodToSend = deworming.method === 'other' ? deworming.customMethod.toLowerCase() : deworming.method;

            const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings/${dewormingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ method: methodToSend, type: deworming.type }),
            });

            if (!response.ok) {
                throw new Error(t('deworming.updateFailed')); // Use translation for error
            }

            navigate(`/pet/${id}/deworming/${dewormingId}`);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDeworming = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings/${dewormingId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(t('deworming.deleteFailed')); // Use translation for error
            }

            navigate(`/pet/${id}`);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="edit-deworming-page bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="text-dafauna-darkText text-2xl font-bold text-center mb-4">{t('deworming.titleEdit')}</h2>
            <form onSubmit={handleUpdateDeworming} className="space-y-4">
                <div className="form-group">
                    <label htmlFor="method" className="block text-dafauna-darkText font-medium mb-2">
                        {t('deworming.method')}
                    </label>
                    <select
                        id="method"
                        name="method"
                        value={deworming.method}
                        onChange={handleInputChange}
                        required
                        className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    >
                        <option value="">{t('deworming.selectMethod')}</option>
                        {dewormingMethods.map((method) => (
                            <option key={method.value} value={method.value}>
                                {method.label[i18n.language]} {/* Fetch the label based on the current language */}
                            </option>
                        ))}
                        <option value="other">{t('deworming.other')}</option>
                    </select>
                </div>
                {deworming.method === 'other' && (
                    <div className="form-group">
                        <label htmlFor="customMethod" className="block text-dafauna-darkText font-medium mb-2">
                            {t('deworming.customMethod')}
                        </label>
                        <input
                            type="text"
                            id="customMethod"
                            name="customMethod"
                            placeholder={t('deworming.enterCustomMethod')}
                            value={deworming.customMethod}
                            onChange={handleInputChange}
                            required
                            className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                        />
                    </div>
                )}
                <div className="form-group">
                    <label htmlFor="type" className="block text-dafauna-darkText font-medium mb-2">
                        {t('deworming.type')}
                    </label>
                    <select
                        id="type"
                        name="type"
                        value={deworming.type}
                        onChange={handleInputChange}
                        required
                        className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    >
                        <option value="">{t('deworming.selectType')}</option>
                        {dewormingTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label[i18n.language]} {/* Fetch the label based on the current language */}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn update-deworming-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    {loading ? t('deworming.updating') : t('deworming.update')}
                </button>
            </form>
            <button
                onClick={handleDeleteDeworming}
                disabled={loading}
                className="btn delete-deworming-btn w-full bg-red-500 text-white rounded-md px-4 py-2 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed mt-4"
            >
                {loading ? t('deworming.deleting') : t('deworming.delete')}
            </button>
        </div>
    );
};

export default EditDewormingPage;